import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import VendorLayout from "../../components/VendorLayout";
import VendreContactFaq from "./VendreContactFaq";
import VendreWizard from "./VendreWizard";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Globe, Copy, ExternalLink, CheckCircle2, AlertTriangle, Sparkles, EyeOff, ArrowLeft, ChevronRight, Palette, Search, MessageCircle, Rocket, Wand2, Megaphone } from "lucide-react";
import VendreMarketing from "../../components/VendreMarketing";
import { VendrePreview } from "../../components/VendrePreview";

const THEMES = [
  {
    key: "premium",
    label: "Premium",
    desc: "Crème & noir, élégance minimale, typographie serif. Inspiration OpenAI.",
    preview: (
      <div className="h-28 rounded-xl bg-[#F6F4EF] border border-[#E8E4D8] overflow-hidden">
        <div className="h-7 border-b border-[#E8E4D8] flex items-center px-3 justify-between">
          <div className="h-2 w-12 rounded bg-[#181713]" />
          <div className="h-2 w-8 rounded-full bg-[#181713]" />
        </div>
        <div className="p-3 grid grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-1">
              <div className="h-8 rounded bg-white border border-[#EAE6DB]" />
              <div className="h-1.5 w-3/4 rounded bg-[#C9C3B2]" />
              <div className="h-1.5 w-1/2 rounded bg-[#E3DFD3]" />
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    key: "stripe",
    label: "Startup",
    desc: "Sombre, dégradés violets, moderne et vivant. Style tech premium.",
    preview: (
      <div className="h-28 rounded-xl bg-slate-950 overflow-hidden">
        <div className="h-9 bg-gradient-to-r from-violet-600 to-fuchsia-600 flex items-center px-3 justify-between">
          <div className="h-2 w-12 rounded bg-white/90" />
          <div className="h-2 w-8 rounded bg-white/40" />
        </div>
        <div className="p-3 grid grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-1">
              <div className="h-8 rounded bg-white/10" />
              <div className="h-1.5 w-3/4 rounded bg-violet-400/60" />
              <div className="h-1.5 w-1/2 rounded bg-white/20" />
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    key: "fun",
    label: "Fun",
    desc: "Chaleureux, orange & rose, formes rondes. Convivial et accessible.",
    preview: (
      <div className="h-28 rounded-xl bg-[#FFF8F0] border-2 border-orange-100 overflow-hidden">
        <div className="h-7 border-b-2 border-orange-100 flex items-center px-3 justify-between">
          <div className="h-2 w-12 rounded-full bg-orange-500" />
          <div className="h-3 w-10 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
        </div>
        <div className="p-3 grid grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-1">
              <div className="h-8 rounded-lg bg-white border-2 border-orange-100" />
              <div className="h-1.5 w-3/4 rounded-full bg-orange-300" />
              <div className="h-1.5 w-1/2 rounded-full bg-orange-100" />
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    key: "pro",
    label: "Professionnel",
    desc: "Bleu corporate, sobre et rassurant. Le classique de la location.",
    preview: (
      <div className="h-28 rounded-xl bg-white border border-slate-200 overflow-hidden">
        <div className="h-7 border-b border-slate-200 flex items-center px-3 justify-between">
          <div className="h-2 w-12 rounded bg-blue-700" />
          <div className="h-3 w-10 rounded bg-blue-700" />
        </div>
        <div className="p-3 grid grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-1">
              <div className="h-8 rounded bg-slate-50 border border-slate-200" />
              <div className="h-1.5 w-3/4 rounded bg-blue-200" />
              <div className="h-1.5 w-1/2 rounded bg-slate-200" />
            </div>
          ))}
        </div>
      </div>
    ),
  },
];

const LEGACY_THEME = { junago: "stripe", minimal: "premium" };

const BOXES = [
  { key: "template", label: "Template", desc: "Le style visuel de votre page — 4 designs au choix.", icon: Palette, subs: ["Premium", "Startup", "Fun", "Professionnel"] },
  { key: "contenu", label: "Contenu & SEO", desc: "Mots clés, description, favicon et référencement Google.", icon: Search, subs: ["Mots clés", "Description IA", "Logo", "Favicon"] },
  { key: "contactfaq", label: "Contact & FAQ", desc: "Vos coordonnées, réseaux sociaux et questions fréquentes.", icon: MessageCircle, subs: ["WhatsApp", "Réseaux", "FAQ IA"] },
  { key: "publication", label: "Publication", desc: "Mettez votre page en ligne et récupérez votre lien.", icon: Rocket, subs: ["Publier", "Lien"] },
  { key: "marketing", label: "Marketing & avis", desc: "QR code, partage et avis clients pour booster vos réservations.", icon: Megaphone, subs: ["QR code", "Partage", "Avis clients"] },
];

function FaviconField({ shop, onSaved }) {
  const [busy, setBusy] = useState(false);
  const favicon = shop.favicon_data_url || "";

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024) { toast.error("Fichier trop lourd (max 200 Ko). Utilisez un PNG carré de 64 à 256 px."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        await api.put("/vendor/settings", { shop: { favicon_data_url: reader.result } });
        toast.success("Favicon enregistré");
        onSaved();
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
      setBusy(false);
    };
    reader.readAsDataURL(file);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.put("/vendor/settings", { shop: { favicon_data_url: "" } });
      toast.success("Favicon supprimé");
      onSaved();
    } catch { toast.error("Erreur"); }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="vendre-favicon-field">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
          {favicon ? <img src={favicon} alt="Favicon" className="h-6 w-6 object-contain" data-testid="vendre-favicon-preview" /> : <Globe className="h-4 w-4 text-slate-300" />}
        </div>
        <div className="flex-1 min-w-0 text-xs text-slate-600">
          <span className="font-bold text-slate-800">Favicon</span> — petite icône affichée dans l'onglet du navigateur de votre page publique. Sans favicon, votre logo est utilisé.
        </div>
        <label className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-slate-900 text-white text-[11px] font-bold hover:bg-slate-800 transition-colors cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid="vendre-favicon-upload">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null} {favicon ? "Remplacer" : "Importer"}
          <input type="file" accept="image/png,image/x-icon,image/svg+xml,image/jpeg,image/webp" className="hidden" onChange={onFile} />
        </label>
        {favicon && (
          <button onClick={remove} disabled={busy} className="px-2.5 h-8 rounded-lg border border-slate-200 text-[11px] font-semibold text-slate-500 hover:text-red-600 hover:border-red-200 transition-colors" data-testid="vendre-favicon-remove">
            Supprimer
          </button>
        )}
      </div>
    </div>
  );
}

export default function Vendre() {
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get("section");
  const [settings, setSettings] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [seo, setSeo] = useState(null);
  const [genSeo, setGenSeo] = useState(false);

  const load = () => {
    api.get("/vendor/settings").then(({ data }) => { setSettings(data); setSeo({ keywords: "", description: "", ...(data.shop?.seo || {}) }); });
    api.get("/stripe/connect/status").then(({ data }) => setStripeStatus(data)).catch(() => setStripeStatus({}));
  };
  useEffect(load, []);

  useEffect(() => {
    if (settings && settings.shop?.wizard_done !== true && !sessionStorage.getItem("jn_vendre_wizard_dismissed")) {
      setWizardOpen(true);
    }
  }, [settings]);

  if (!settings) return <VendorLayout><div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div></VendorLayout>;

  const shop = settings.shop || {};
  const pageUrl = shop.slug ? `${window.location.origin}/p/${shop.slug}` : null;

  const configured = {
    template: !!shop.theme,
    contenu: !!(shop.seo?.keywords || shop.seo?.description),
    contactfaq: !!(Object.values(shop.contact || {}).some(Boolean) || (shop.faq || []).length > 0),
    publication: !!shop.published,
    marketing: !!shop.published,
  };
  const done = Object.values(configured).filter(Boolean).length;
  const percent = Math.round((done / BOXES.length) * 100);

  const setTheme = async (theme) => {
    setSettings((p) => ({ ...p, shop: { ...p.shop, theme } }));
    try { await api.put("/vendor/settings", { shop: { theme } }); } catch { toast.error("Erreur"); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/vendor/shop/publish");
      setSettings((p) => ({ ...p, shop: { ...p.shop, ...data } }));
      toast.success("Votre site est en ligne 🎉");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setBusy(false);
  };

  const unpublish = async () => {
    setBusy(true);
    try {
      await api.post("/vendor/shop/unpublish");
      setSettings((p) => ({ ...p, shop: { ...p.shop, published: false } }));
      toast.success("Page dépubliée");
    } catch { toast.error("Erreur"); }
    setBusy(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(pageUrl);
    toast.success("Lien copié !");
  };

  const saveSeo = async () => {
    try { await api.put("/vendor/settings", { shop: { seo } }); toast.success("Contenu enregistré"); load(); }
    catch { toast.error("Erreur"); }
  };

  const generateSeo = async () => {
    setGenSeo(true);
    try {
      const { data } = await api.post("/vendor/shop/seo/generate", { keywords: seo.keywords });
      setSeo((p) => ({ ...p, description: data.description }));
      toast.success("Description générée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Génération impossible"); }
    setGenSeo(false);
  };

  const openSection = (key) => setSearchParams({ section: key });
  const box = section ? BOXES.find((b) => b.key === section) : null;

  const statusCard = (
    <div className="rounded-2xl bg-slate-900 text-white p-5 sm:p-6" data-testid="vendre-status-card">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[240px]">
          {shop.published ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[11px] font-bold"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> EN LIGNE</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 text-slate-300 text-[11px] font-bold"><EyeOff className="h-3 w-3" /> Non publiée</span>
          )}
          {shop.published && pageUrl && (
            <div className="mt-3 flex items-center gap-2">
              <code className="px-3 py-1.5 rounded-lg bg-white/10 text-xs text-violet-200 truncate" data-testid="vendre-page-url">{pageUrl}</code>
              <button onClick={copyLink} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" data-testid="vendre-copy-link" title="Copier le lien"><Copy className="h-3.5 w-3.5" /></button>
              <a href={pageUrl} target="_blank" rel="noreferrer" className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" data-testid="vendre-open-page" title="Voir ma page"><ExternalLink className="h-3.5 w-3.5" /></a>
            </div>
          )}
        </div>
        {shop.published ? (
          <button onClick={unpublish} disabled={busy} className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-bold transition-colors disabled:opacity-50" data-testid="vendre-unpublish">Dépublier</button>
        ) : (
          <button onClick={publish} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-bold transition-colors disabled:opacity-50" data-testid="vendre-publish">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} Publier ma page
          </button>
        )}
      </div>
    </div>
  );

  return (
    <VendorLayout>
      <div className="min-h-screen bg-slate-50">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6" data-testid="vendre-page">
          {box ? (
            <>
              <button onClick={() => setSearchParams({})} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-violet-700 transition-colors mb-4" data-testid="vendre-back-to-hub">
                <ArrowLeft className="h-3.5 w-3.5" /> Mon site internet
              </button>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center"><box.icon className="h-5 w-5" /></div>
                <div>
                  <h1 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">{box.label}</h1>
                  <div className="text-xs text-slate-500">{box.desc}</div>
                </div>
              </div>

              {section === "template" && (
                <div className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    {THEMES.map((t) => {
                    const active = (LEGACY_THEME[shop.theme] || shop.theme) === t.key;
                    return (
                      <button key={t.key} onClick={() => setTheme(t.key)}
                        className={`text-left rounded-2xl border-2 p-4 transition-all ${active ? "border-violet-500 bg-violet-50/50 shadow-lg shadow-violet-100" : "border-slate-200 bg-white hover:border-violet-300"}`}
                        data-testid={`vendre-theme-${t.key}`}>
                        {t.preview}
                        <div className="mt-3 flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{t.label}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{t.desc}</div>
                          </div>
                          {active && <CheckCircle2 className="h-5 w-5 text-violet-600 shrink-0" />}
                        </div>
                      </button>
                    );
                  })}
                  </div>
                  <VendrePreview refreshKey={shop.theme} />
                </div>
              )}

              {section === "contenu" && seo && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-5" data-testid="vendre-seo-card">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">Vos mots clés</label>
                    <input value={seo.keywords} onChange={(e) => setSeo((p) => ({ ...p, keywords: e.target.value }))} placeholder="location de voiture Guadeloupe, SUV, aéroport…"
                      className="w-full h-11 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-400" data-testid="vendre-seo-keywords" />
                  </div>
                  <div className="rounded-xl bg-violet-50/60 border border-violet-100 p-4 flex items-start gap-2.5">
                    <Sparkles className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                    <div className="text-xs text-slate-600 leading-relaxed">
                      <span className="font-bold text-slate-800">Conseil Junago :</span> mettez en avant votre <span className="font-semibold">activité principale + votre ville</span> — ex. « location de voiture Guadeloupe ». C'est ce que vos clients tapent sur Google.
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Description de votre page</label>
                      <button onClick={generateSeo} disabled={genSeo} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-violet-600 text-white text-[11px] font-bold hover:bg-violet-500 transition-colors disabled:opacity-50" data-testid="vendre-seo-generate">
                        {genSeo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} Générer avec l'IA
                      </button>
                    </div>
                    <textarea value={seo.description} onChange={(e) => setSeo((p) => ({ ...p, description: e.target.value }))} rows={3}
                      className="w-full p-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-400" data-testid="vendre-seo-description" />
                  </div>
                  <FaviconField shop={shop} onSaved={load} />
                  {settings.company?.logo_data_url ? (
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3 flex items-center gap-3 text-xs text-emerald-700">
                      <img src={settings.company.logo_data_url} alt="Logo" className="h-9 w-9 rounded-lg object-contain bg-white p-0.5" />
                      <span><span className="font-bold">Logo configuré</span> — repris depuis Paramétrages → Entreprise.</span>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-800">
                      <span className="font-bold">Logo manquant.</span> Ajoutez-le dans <a href="/vendor/parametrages?section=company" className="underline font-semibold">Paramétrages → Entreprise</a>.
                    </div>
                  )}
                  <button onClick={saveSeo} className="px-5 h-10 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-colors" data-testid="vendre-seo-save">Enregistrer</button>
                </div>
              )}

              {section === "contactfaq" && <VendreContactFaq settings={settings} reload={load} />}

              {section === "publication" && (
                <div className="space-y-6">
                  {statusCard}
                </div>
              )}

              {section === "marketing" && (
                <VendreMarketing pageUrl={pageUrl} published={!!shop.published} />
              )}
            </>
          ) : (
            <>
              <div className="overline">Vendre en ligne</div>
              <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-slate-900 mt-1">Mon site internet de réservation</h1>
              <p className="text-sm text-slate-500 mt-2 max-w-2xl">Une page web à votre marque — votre logo, vos véhicules, la réservation et le paiement en ligne. Partagez le lien, encaissez.</p>

              {!stripeStatus?.stripe_onboarded && (
                <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="vendre-stripe-warning">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-800">
                    <span className="font-semibold">Paiements non activés.</span> Activez Stripe pour encaisser en ligne :{" "}
                    <a href="/vendor/parametrages?section=payments" className="underline font-semibold">Paramétrages → Paiements</a>
                  </div>
                </div>
              )}

              {/* Progress card */}
              <div className="mt-6 rounded-2xl bg-slate-900 text-white p-5 sm:p-6" data-testid="vendre-progress-card">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-violet-300" />
                      <div className="text-sm font-semibold">Configuration de votre page web</div>
                      <div className="ml-auto sm:ml-0 text-sm font-bold text-violet-300" data-testid="vendre-progress-percent">{percent} %</div>
                    </div>
                    <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-700" style={{ width: `${percent}%` }} />
                    </div>
                    {shop.published && pageUrl && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-300"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> EN LIGNE</span>
                        <code className="px-2.5 py-1 rounded-lg bg-white/10 text-[11px] text-violet-200 truncate">{pageUrl}</code>
                        <button onClick={copyLink} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" data-testid="vendre-copy-link"><Copy className="h-3 w-3" /></button>
                        <a href={pageUrl} target="_blank" rel="noreferrer" className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" data-testid="vendre-open-page"><ExternalLink className="h-3 w-3" /></a>
                      </div>
                    )}
                  </div>
                  <button onClick={() => setWizardOpen(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold transition-colors shrink-0" data-testid="vendre-launch-wizard">
                    <Sparkles className="h-3.5 w-3.5" /> Configuration guidée
                  </button>
                </div>
              </div>

              {/* Boxes — grille plein écran */}
              <div className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
                {BOXES.map((b, i) => {
                  const ok = configured[b.key];
                  return (
                    <button key={b.key} onClick={() => openSection(b.key)}
                      className="group relative text-left rounded-[22px] border border-slate-200 bg-white p-5 lg:p-6 overflow-hidden hover:border-violet-300 hover:shadow-xl hover:shadow-violet-100/60 hover:-translate-y-1 transition-all duration-300"
                      style={{ animation: `jnBoxIn .5s ${i * 70}ms cubic-bezier(.2,.8,.3,1) both` }}
                      data-testid={`vendre-box-${b.key}`}>
                      <div className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-violet-100/40 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="flex items-start justify-between gap-3">
                        <div className="h-12 w-12 rounded-2xl bg-slate-900 text-white group-hover:bg-violet-600 flex items-center justify-center transition-colors">
                          <b.icon className="h-5 w-5" />
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{ok ? "Complet" : "À configurer"}</span>
                      </div>
                      <div className="mt-4 font-heading text-lg font-semibold text-slate-900">{b.label}</div>
                      <div className="mt-1 text-xs text-slate-500 leading-relaxed">{b.desc}</div>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {b.subs.map((sub) => <span key={sub} className="px-2 py-0.5 rounded-full bg-slate-50 border border-slate-100 text-[10px] font-medium text-slate-500">{sub}</span>)}
                      </div>
                      <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-violet-700 opacity-0 group-hover:opacity-100 transition-opacity">Ouvrir <ChevronRight className="h-3 w-3" /></div>
                    </button>
                  );
                })}
              </div>

              {/* Aperçu en direct de la page publique */}
              <div className="mt-6">
                <VendrePreview refreshKey={`${shop.theme}-${shop.published ? 1 : 0}-${shop.favicon_data_url ? 1 : 0}`} />
              </div>
              <style>{`@keyframes jnBoxIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}`}</style>
            </>
          )}
        </div>
      </div>

      {wizardOpen && settings && (
        <VendreWizard
          settings={settings}
          themes={THEMES}
          legacyTheme={LEGACY_THEME}
          onClose={() => { sessionStorage.setItem("jn_vendre_wizard_dismissed", "1"); setWizardOpen(false); }}
          onFinished={() => { setWizardOpen(false); load(); }}
        />
      )}
    </VendorLayout>
  );
}
